import { BigNumberExt } from "$root/third-party-integrations/bigNumber";
import { LAB_MATH_PRECISION, MAX_PERCENT_DECIMAL_PLACES } from "$root/utils/monetary-math";

/**
 * The generic monetary/precision arithmetic lives in `$root/utils/monetary-math` so that
 * non-lab features (e.g. the instruments editor) can use it without importing lab
 * modules (which would create an import cycle — the lab already imports from
 * `instruments-editor/builder`). Re-exported here so lab call sites keep working
 * unchanged; lab-only constants and helpers stay below.
 */
export {
	LAB_MATH_PRECISION,
	labAdd,
	labDiv,
	labMul,
	labSub,
	labSum,
	truncateToPrecision,
} from "$root/utils/monetary-math";

/**
 * Maximum AUM the lab supports as user input: $10 trillion.
 *
 * This is where the product cap and the technical ceiling coincide. Three IEEE-754
 * constraints bound the system; the tightest wins:
 *
 * 1. Weight representability — weights live near 100 %, where double spacing (ULP)
 *    ≈ 1.42×10⁻¹⁴; the finest reliably distinct decimal step is 10⁻¹³ → 13 usable
 *    decimal places → AUM ≤ 10¹³ (see {@link weightPrecisionForAum}).
 * 2. Monetary cent-safe rounding — `.dp(2, ROUND_HALF_UP)` needs ULP ≤ half a cent,
 *    i.e. value < 2⁴⁵ → AUM ≤ ~$35.2T.
 * 3. Integer-cent exactness (2⁵³ − 1 cents) → AUM ≤ ~$90T.
 *
 * Constraint 1 binds: at 1e13 every cent is expressible through the weight grid,
 * weights remain distinctly representable, and {@link LAB_MATH_PRECISION} = 14 stays
 * one notch above as the float-noise strip. Product sanity: the largest real single
 * funds are ≈ $1.4–1.8T, so $10T gives >5× headroom. API-provided AUM above the cap
 * is NOT rejected — {@link weightPrecisionForAum} clamps, gracefully degrading to
 * sub-cent fidelity loss (calculations never corrupt below the ~$35T float limit).
 */
export const MAX_LAB_AUM = 1e13;

/**
 * Floor for the dynamic weight precision: the historical fixed `WEIGHT_PRECISION = 4`.
 * Behavior never gets coarser than it used to be — AUM ≤ $10k behaves identically.
 */
export const MIN_WEIGHT_PRECISION = 4;

/**
 * Ceiling for the dynamic weight precision: `ceil(log10(MAX_LAB_AUM))` = 13, the
 * {@link MAX_PERCENT_DECIMAL_PLACES} a weight near 100 can carry.
 * Deliberately NOT 14 ({@link LAB_MATH_PRECISION}) — the 14th decimal place is below
 * the double ULP for weights in [64, 100], so a 14th digit would not be reliably
 * representable and could silently collapse distinct weights.
 */
export const MAX_WEIGHT_PRECISION = MAX_PERCENT_DECIMAL_PLACES;

/**
 * Decimal places (percent) a weight must carry so the minimum monetary value — one
 * cent — is expressible through the weight grid at the given AUM.
 *
 * A weight stored at `p` decimal places (percent) has a monetary granularity of
 * `aum × 10^-(p+2)`; cent-accuracy therefore needs `p = ceil(log10(aum))`
 * (e.g. $1M → 6 dp, $100B → 11 dp, $10T → 13 dp). `Math.ceil` makes `log10` float
 * error harmless: it can only over-provision by 1 dp, never under. Null or
 * non-positive AUM falls back to {@link MIN_WEIGHT_PRECISION}.
 */
export function weightPrecisionForAum(aum: number | null): number {
	if (aum == null || aum <= 0) {
		return MIN_WEIGHT_PRECISION;
	}
	return Math.min(Math.max(Math.ceil(Math.log10(aum)), MIN_WEIGHT_PRECISION), MAX_WEIGHT_PRECISION);
}

/**
 * Rounds a number to {@link LAB_MATH_PRECISION} decimal places, stripping IEEE-754 noise.
 * Non-finite values (`NaN`, `±Infinity`) pass through unchanged.
 */
export function roundLabNumber(value: number): number {
	return BigNumberExt.roundWithPrecision(value, LAB_MATH_PRECISION);
}
